import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
    LayoutDashboard,
    KanbanSquare,
    Users,
    UserCheck,
    GraduationCap,
    Settings,
    LogOut,
    ChevronLeft,
    Building2,
    Newspaper,
    ChevronDown,
    User,
    UserPlus,
    ListTodo,
    CalendarDays,
    Wrench,
    LifeBuoy,
    Sparkles,
    Lightbulb,
    ClipboardList,
    FileText,
    ExternalLink,
    Gauge,
    Network,
    FolderKanban,
    CheckSquare,
    Mail,
    Rocket,
    ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { hubAPI } from '@/lib/api';
import { useUsefulDocsUnread } from '@/hooks/useUsefulDocsUnread';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { GlobalSearch } from '@/components/common/GlobalSearch';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ROLE_NAMES = {
    admin_full: 'Admin Full',
    admin: 'Admin',
    comptable: 'Comptable',
    vp: 'VP',
    apporteur: 'Apporteur'
};

const TOP_ITEMS = [
    { icon: Rocket, label: 'Projets', path: '/projets', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'parrain'], requiresHubAccess: true, isNew: true },
    { icon: Newspaper, label: "Fil d'actualité", path: '/feed', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: Sparkles, label: 'Inside VNX', path: '/inside-vnx', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
];

const NAV_GROUPS = [
    {
        id: 'dossiers', label: 'Dossiers', icon: FolderKanban,
        items: [
            { icon: Users, label: 'Prospects', path: '/prospects', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'parrain'] },
            { icon: UserCheck, label: 'Clients', path: '/clients', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
            { icon: KanbanSquare, label: 'Pipeline', path: '/pipeline', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
        ],
    },
    {
        id: 'equipe', label: "Gestion d'équipe", icon: Building2,
        items: [
            { icon: UserPlus, label: 'Onboarding', path: '/onboarding', roles: ['admin_full', 'admin', 'vp'] },
            { icon: Network, label: 'Réseau Parrainage', path: '/parrainage', roles: ['admin_full', 'admin', 'vp', 'parrain'], requiresParrainage: true, isNew: true },
            { icon: Gauge, label: 'Implication', path: '/implication', roles: ['admin_full', 'admin', 'vp'], isNew: true },
            { icon: Building2, label: 'Équipes', path: '/teams', roles: ['admin_full', 'admin', 'vp'] },
            { icon: FileText, label: 'Candidatures', href: 'https://valnexo.fr/admin/candidatures', external: true, roles: ['admin_full', 'admin', 'vp'], isNew: true },
            { icon: Mail, label: 'Demandes officielles', path: '/demandes-officielles', roles: ['admin_full', 'admin'], dirigeantOnly: true, isNew: true },
        ],
    },
    {
        id: 'actions', label: 'Actions', icon: CheckSquare,
        items: [
            { icon: ListTodo, label: 'Tâches', path: '/tasks', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
            { icon: CalendarDays, label: 'Calendrier', path: '/calendar', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
        ],
    },
    {
        id: 'supports', label: 'Supports', icon: LifeBuoy,
        items: [
            { icon: GraduationCap, label: 'Académie', path: '/academy', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'apprenant', 'parrain'] },
            { icon: Lightbulb, label: 'Boîte à idées', path: '/boite-a-idees', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'parrain'], isNew: true },
            { icon: Wrench, label: 'Outils & liens utiles', path: '/outils', roles: ['admin_full', 'admin', 'comptable', 'vp', 'apporteur', 'parrain'] },
            { icon: Mail, label: 'Demande officielle', path: '/demandes-officielles', roles: ['admin_full', 'admin', 'vp'], vpOnly: true, isNew: true },
            { icon: LifeBuoy, label: 'Support', path: '/support', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'apprenant', 'parrain'] },
        ],
    },
];

const BOTTOM_ITEMS = [
    { icon: Settings, label: 'Paramètres', path: '/settings', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'parrain'] },
];

// Composant Avatar utilisateur
const UserAvatar = ({ user, size = 'md' }) => {
    const sizeClasses = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base'
    };

    const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
    const avatarUrl = user?.avatar_url || user?.profile_picture;

    if (avatarUrl) {
        return (
            <img
                src={avatarUrl}
                alt={`${user?.first_name} ${user?.last_name}`}
                className={cn(
                    "rounded-full object-cover border-2 border-white/20",
                    sizeClasses[size]
                )}
            />
        );
    }

    if (initials) {
        return (
            <div className={cn(
                "rounded-full bg-gradient-to-br from-yellow-600 to-yellow-500 flex items-center justify-center font-semibold text-white border-2 border-white/20",
                sizeClasses[size]
            )}>
                {initials}
            </div>
        );
    }

    return (
        <div className={cn(
            "rounded-full bg-slate-600 flex items-center justify-center border-2 border-white/20",
            sizeClasses[size]
        )}>
            <User className="w-1/2 h-1/2 text-slate-300" />
        </div>
    );
};

// Pastille « Nouveau » animée (disparaît au premier clic, mémorisée par utilisateur)
const NewBadge = () => (
    <span
        className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-2 py-0.5 text-[10px] font-semibold text-yellow-400 ring-1 ring-yellow-500/40"
        data-testid="nav-new-badge"
    >
        <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-yellow-400" />
        </span>
        Nouveau
    </span>
);

const SEEN_NAV_KEY = 'vnx_seen_nav_v1';

export const Sidebar = () => {
    const { user, logout, isTrainingRestricted, unreadFeedCount, wallUnreadCount } = useAuth();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const usefulDocsUnread = useUsefulDocsUnread(user?.id);
    const [hubUnread, setHubUnread] = useState(0);

    // Compteur global de non-lus sur les projets Hub (badge sidebar Projets)
    useEffect(() => {
        let alive = true;
        const fetchHub = async () => {
            try {
                const r = await hubAPI.unreadCount();
                if (alive) setHubUnread(r.data?.count || 0);
            } catch { /* noop */ }
        };
        fetchHub();
        const iv = setInterval(fetchHub, 45000);
        // Rafraîchit immédiatement lorsque le fil d'activité vient d'être marqué comme lu
        const onSeen = () => fetchHub();
        window.addEventListener('hub:activity-seen', onSeen);
        return () => { alive = false; clearInterval(iv); window.removeEventListener('hub:activity-seen', onSeen); };
    }, []);

    const [seenNav, setSeenNav] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SEEN_NAV_KEY) || '[]'); } catch { return []; }
    });

    const markNavSeen = (id) => {
        setSeenNav((prev) => {
            if (prev.includes(id)) return prev;
            const next = [...prev, id];
            try { localStorage.setItem(SEEN_NAV_KEY, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
    };

    // Aligné avec _is_admin() du backend : accepte le flag ou le role admin_full/admin
    const userHasAdminAccess = user?.is_admin === true || user?.role === 'admin_full' || user?.role === 'admin';
    const hubAccessLevel = user?.hub_access || 'none';
    const userHasHubAccess = userHasAdminAccess || hubAccessLevel === 'full' || hubAccessLevel === 'restricted';
    const DIRIGEANT_EMAILS = ['jeremy@valnexo.fr', 'morgan@valnexo.fr', 'laurent@valnexo.fr'];
    const userEmail = (user?.email || '').toLowerCase();
    const isDirigeant = DIRIGEANT_EMAILS.includes(userEmail);
    const isItemVisible = (item) => {
        const roleAllowed = item.roles.includes(user?.role)
            || (userHasAdminAccess && (item.roles.includes('admin_full') || item.roles.includes('admin')));
        if (!roleAllowed) return false;
        if (item.requiresParrainage) {
            // Réseau Parrainage activé par défaut pour TOUS les VP (plus de switch).
            // On garde le drapeau pour la rétro-compat mais on ne filtre plus les VP.
        }
        // Hub Projets : réservé aux utilisateurs avec accès explicite (ou admin)
        if (item.requiresHubAccess && !userHasHubAccess) return false;
        // dirigeantOnly : réservé aux 3 dirigeants (Jérémy, Morgan, Laurent) identifiés par email
        if (item.dirigeantOnly && !isDirigeant) return false;
        // vpOnly : masqué pour les 3 dirigeants (ils ont leur propre entrée sous Gestion d'équipe)
        if (item.vpOnly && isDirigeant) return false;
        if (isTrainingRestricted() && !['/academy', '/support'].includes(item.path)) return false;
        return true;
    };

    const topItems = TOP_ITEMS.filter(isItemVisible);
    const bottomItems = BOTTOM_ITEMS.filter(isItemVisible);
    const visibleGroups = NAV_GROUPS
        .map(g => ({ ...g, items: g.items.filter(isItemVisible) }))
        .filter(g => g.items.length > 0);

    // Groupes dépliables : ouvre par défaut le groupe contenant la page active
    const [openGroups, setOpenGroups] = useState({});
    useEffect(() => {
        const activeGroup = NAV_GROUPS.find(g => g.items.some(it => it.path && location.pathname.startsWith(it.path)));
        if (activeGroup) setOpenGroups(prev => ({ ...prev, [activeGroup.id]: true }));
    }, [location.pathname]);
    const toggleGroup = (id) => setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

    const renderNavLink = (item, { nested = false } = {}) => {
        const isActive = item.path && location.pathname === item.path;
        const showFeedBadge = item.path === '/feed' && unreadFeedCount > 0;
        const showWallBadge = item.path === '/prospects' && wallUnreadCount > 0;
        const showDocsBadge = item.path === '/outils' && usefulDocsUnread > 0;
        const showHubBadge = item.path === '/projets' && hubUnread > 0;
        const badgeCount = showFeedBadge ? unreadFeedCount : (showWallBadge ? wallUnreadCount : (showDocsBadge ? usefulDocsUnread : (showHubBadge ? hubUnread : 0)));
        const showBadge = showFeedBadge || showWallBadge || showDocsBadge || showHubBadge;
        const badgeKey = showWallBadge ? 'wall' : (showDocsBadge ? 'useful-docs' : (showHubBadge ? 'hub' : 'feed'));
        const navId = item.path || item.href;
        const showNew = item.isNew && !seenNav.includes(navId);
        const padClass = nested ? 'pl-9 pr-3 py-2' : 'px-3 py-2.5';

        if (item.external) {
            return (
                <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => markNavSeen(navId)}
                    data-testid="sidebar-candidatures-link"
                    className={cn("flex items-center gap-3 rounded-xl transition-all duration-200 hover:bg-white/10 group", padClass)}
                >
                    <div className="relative">
                        <item.icon className="w-5 h-5 transition-colors text-slate-400 group-hover:text-white" />
                        {showNew && collapsed && (
                            <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-400" />
                            </span>
                        )}
                    </div>
                    {!collapsed && <span className="font-medium transition-colors flex-1 text-slate-400 group-hover:text-white">{item.label}</span>}
                    {!collapsed && showNew && <NewBadge />}
                    {!collapsed && !showNew && <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300" />}
                </a>
            );
        }

        return (
            <Link
                key={item.path}
                to={item.path}
                reloadDocument={item.path === '/inside-vnx'}
                onClick={() => showNew && markNavSeen(navId)}
                data-testid={`sidebar-link-${(item.path || '').replace('/', '')}`}
                className={cn(
                    "flex items-center gap-3 rounded-xl transition-all duration-200 hover:bg-white/10 group",
                    padClass,
                    isActive && "bg-white/10 border-l-4 border-l-yellow-500"
                )}
            >
                <div className="relative">
                    <item.icon className={cn("w-5 h-5 transition-colors", isActive ? "text-yellow-500" : "text-slate-400 group-hover:text-white")} />
                    {showBadge && collapsed && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
                            data-testid={`${badgeKey}-unread-badge-collapsed`}>
                            {badgeCount > 9 ? '9+' : badgeCount}
                        </span>
                    )}
                    {showNew && !showBadge && collapsed && (
                        <span className="absolute -top-1 -right-1 flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-400" />
                        </span>
                    )}
                </div>
                {!collapsed && (
                    <span className={cn("font-medium transition-colors flex-1", isActive ? "text-white" : "text-slate-400 group-hover:text-white")}>
                        {item.label}
                    </span>
                )}
                {showBadge && !collapsed && (
                    <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold leading-none"
                        data-testid={`${badgeKey}-unread-badge`}>
                        {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                )}
                {showNew && !showBadge && !collapsed && <NewBadge />}
            </Link>
        );
    };

    return (
        <>
            {/* TOP HEADER BAR - Fixed en haut avec profil utilisateur à droite */}
            <header className="hidden md:flex fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-40 items-center justify-between px-6" style={{ marginLeft: collapsed ? '80px' : '256px' }}>
                {/* Barre de recherche universelle */}
                <GlobalSearch />

                {/* Notifications + Profil utilisateur à droite */}
                <div className="flex items-center gap-2">
                    <NotificationCenter />

                    <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                            aria-label="Ouvrir le menu utilisateur"
                            data-testid="user-menu-trigger-header"
                        >
                            <UserAvatar user={user} size="md" />
                            <div className="text-left">
                                <p className="text-sm font-medium text-slate-900">
                                    {user?.first_name} {user?.last_name?.toUpperCase()}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {ROLE_NAMES[user?.role] || user?.role}
                                </p>
                            </div>
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <div className="px-3 py-2 border-b">
                            <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                            <p className="text-xs text-slate-500">{user?.email}</p>
                        </div>
                        <DropdownMenuItem asChild>
                            <Link to="/settings" className="cursor-pointer">
                                <Settings className="w-4 h-4 mr-2" />
                                Paramètres
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={logout}
                            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Déconnexion
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                </div>
            </header>

            {/* SIDEBAR - Menu de navigation à gauche */}
            <aside className={cn(
                "hidden md:flex flex-col fixed inset-y-0 left-0 z-50 transition-all duration-300",
                "bg-slate-900/95 backdrop-blur-xl border-r border-white/5",
                collapsed ? "w-20" : "w-64"
            )}>
                {/* Logo */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
                    <Link to="/dashboard" className="flex items-center gap-3">
                        {/* Valnexo Logo - Image officielle */}
                        <img
                            src="/valnexo-logo-official-new.png"
                            alt="Valnexo Logo"
                            className="w-9 h-9 object-contain rounded"
                        />
                        {!collapsed && (
                            <span className="font-bold text-xl text-white tracking-tight">
                                Valnexo
                            </span>
                        )}
                    </Link>
                    <div className="flex items-center gap-1">
                        <NotificationCenter bellTestId="sidebar-notification-bell" />
                        <button
                            onClick={() => setCollapsed(!collapsed)}
                            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                        >
                            <ChevronLeft className={cn(
                                "w-5 h-5 text-slate-400 transition-transform",
                                collapsed && "rotate-180"
                            )} />
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
                    {/* Onglets simples */}
                    {topItems.map((item) => renderNavLink(item))}

                    {/* Groupes dépliables (ou liste à plat quand la sidebar est repliée) */}
                    {visibleGroups.map((group) => {
                        if (collapsed) {
                            return (
                                <div key={group.id} className="pt-1">
                                    {group.items.map((item) => renderNavLink(item))}
                                </div>
                            );
                        }
                        const isOpen = !!openGroups[group.id];
                        const hasActive = group.items.some(it => it.path && location.pathname === it.path);
                        return (
                            <div key={group.id} className="pt-2">
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(group.id)}
                                    data-testid={`sidebar-group-${group.id}`}
                                    aria-expanded={isOpen}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 group",
                                        "hover:bg-white/5",
                                        hasActive ? "text-white" : "text-slate-400"
                                    )}
                                >
                                    <group.icon className={cn("w-5 h-5 transition-colors", hasActive ? "text-yellow-500" : "text-slate-500 group-hover:text-white")} />
                                    <span className="font-semibold text-xs uppercase tracking-wide flex-1 text-left">{group.label}</span>
                                    <ChevronRight className={cn("w-4 h-4 text-slate-500 transition-transform", isOpen && "rotate-90")} />
                                </button>
                                <div className={cn(
                                    "overflow-hidden transition-all duration-200 space-y-1",
                                    isOpen ? "max-h-[600px] mt-1 opacity-100" : "max-h-0 opacity-0"
                                )}>
                                    {group.items.map((item) => renderNavLink(item, { nested: true }))}
                                </div>
                            </div>
                        );
                    })}

                    {/* Paramètres en bas */}
                    {bottomItems.length > 0 && (
                        <div className="pt-3 mt-2 border-t border-white/10">
                            {bottomItems.map((item) => renderNavLink(item))}
                        </div>
                    )}
                </nav>
            </aside>
        </>
    );
};
